// Investor Outreach card on the Account page (ATAI-271).
// Figma: node 925:52250 (not subscribed), 925:52526 (active), 1184:105936 (Manage Plan open).
// Subscription state: useInvestorOutreachEntitlement.js (primary holder). Team member: profile.allowInvestorOutreach + profile.investorOutreachSubscribed (S10).
import React, { useState } from 'react';
import classNames from 'classnames';
import * as moment from 'moment';
import SVG from 'components/base/SVG';
import { useToast } from '../../components/ToastMessage/ToastManager';
import { restore } from '../InvestorOutreach/investorOutreachApi';
import { selectUserId } from 'data/user';
import { withRouter } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import css from '../style.scss';
import PopoverMenu from '../../components/BatchdailerMenu/PopoverMenu';
import investorOutreachLogo from 'assets/images/investor-outreach-logo.svg'; // from Figma node 1151:94850 (210x79)
import investorOutreachIllustration from 'assets/images/investor-outreach-illustration.svg'; // downloaded from Figma node 925:51740 (185x91, shared across every card state)
import { selectPlanDetails, selectProfile, isLegacyAccountPlan } from 'data/user/selectors';
import { selectBillingDashboard } from 'data/user';
import ActivateSubscriptionPopup from '../modals/ActivateSubscription';
import UnlockConfirmModal from 'app/InvestorOutreach/UnlockConfirmModal';
import { openLegacyUpgradePopup } from '../InvestorOutreach/legacyUpgradePopup';
import LeaveMessagePopup from '../modals/LeaveMessage';
import LoadablePanel from 'components/hoc/loadable/Panel';
import { useInvestorOutreachEntitlement } from './useInvestorOutreachEntitlement';
import { refreshSubscriptionCards } from '../refreshSubscriptionCards';
import { isPropStream } from 'utils/brand';
import { INVESTOR_SEARCH_PATH } from 'data/investors';

// One route for subscribe and unsubscribe, like Connect's /accountnew/manageleadautomator/:type.
const INVESTOR_OUTREACH_MANAGE_ROUTE = (type) => `/accountnew/manageinvestoroutreach/${type}`;
const INVESTOR_OUTREACH_PURCHASE_ROUTE = INVESTOR_OUTREACH_MANAGE_ROUTE('subscribe');
const INVESTOR_OUTREACH_CANCEL_ROUTE = INVESTOR_OUTREACH_MANAGE_ROUTE('unsubscribe');

const InvestorOutreach = (props) => {
  const [isManagePlanOpen, setIsManagePlanOpen] = useState(false);

  const dispatch = useDispatch();
  const showToast = useToast();
  const userId = useSelector(selectUserId);
  const profile = useSelector(selectProfile)?.toJS();
  const planDetails = useSelector(selectPlanDetails)?.toJS();
  const billingDashboard = useSelector(selectBillingDashboard);
  const isLegacyAccount = useSelector(isLegacyAccountPlan);

  // Per-member permission and the account's subscription state, both from the profile (S10 · ATAI-275).
  const allowInvestorOutreach = Boolean(profile?.allowInvestorOutreach);
  const accountSubscribed = Boolean(profile?.investorOutreachSubscribed);

  const { isFullAccount } = props; // NB: true for TEAM/TEAM_FULL profiles — i.e. this is the *team-member* layout, not the primary holder's. See ATAI-259 comment.

  // Scout check follows ClickToDial: isFullAccount is the team layout and doesn't exclude Scouts.
  const isScout = Boolean(profile?.subUser && profile?.scoutUser);
  const isTeamMember = Boolean(isFullAccount);
  const isPrimaryHolder = !isTeamMember && !isScout;

  const isEligibleBrand = isPropStream();

  // Shown to the primary holder (always) and team members (with the permission, while the account is
  // subscribed: a member can't buy, so there is nothing to show them otherwise). Never to Scouts or white-label.
  const shouldRender =
    isEligibleBrand &&
    !isScout &&
    (isPrimaryHolder || (isTeamMember && allowInvestorOutreach && accountSubscribed));

  // cancel-preview is primary-only; a team member's subscription state comes from the profile instead.
  const {
    ready,
    failed,
    entitled: primaryEntitled,
    status,
    endDate,
    connectCancelledWithIo,
    endedDate,
  } = useInvestorOutreachEntitlement(shouldRender && isPrimaryHolder);

  if (!shouldRender || (isPrimaryHolder && failed)) {
    return null;
  }

  // Spinner until the state is known.
  const isLoading = isPrimaryHolder && !ready;
  const isActive = isPrimaryHolder ? primaryEntitled : accountSubscribed;
  const isPendingCancel = isActive && status === 'UNSUBSCRIBED'; // active + pending-cancellation both read as entitled — see status handling below
  const isTrial = Boolean(planDetails?.trialAvailable);
  // Both end together when Connect was cancelled: the backend says so, or Connect is itself pending cancellation.
  const connectAlsoCancelled =
    isPendingCancel &&
    (connectCancelledWithIo || billingDashboard?.psConnectStatus === 'UNSUBSCRIBED');

  const goToPurchasePage = () => {
    // ATAI-448: a legacy plan can't buy Investor Outreach -- send it to the plan page first.
    if (isLegacyAccount) {
      dispatch(openLegacyUpgradePopup({ onNavigate: (path) => props.history.push(path) }));
      return;
    }

    // On a trial, Subscribe Now goes through trial conversion first (S4 · ATAI-490), the same steps as
    // the left-nav Unlock Now: the "Unlock Investor Outreach" prompt, then Activate Subscription to end
    // the trial, then straight on to the purchase page with no success message in between.
    if (isTrial) {
      dispatch(
        UnlockConfirmModal.open({
          onConfirm: () =>
            dispatch(
              ActivateSubscriptionPopup.open({
                upgradedFromCallSettings: false,
                skipSuccessMessage: true,
                onNavigate: (path) => props.history.push(path),
                onSuccess: () => props.history.push(INVESTOR_OUTREACH_PURCHASE_ROUTE),
              })
            ),
        })
      );
      return;
    }
    props.history.push(INVESTOR_OUTREACH_PURCHASE_ROUTE);
  };

  const goToInvestorOutreach = () => {
    props.history.push(INVESTOR_SEARCH_PATH);
  };

  const handleUnsubscribe = () => {
    setIsManagePlanOpen(false);
    props.history.push(INVESTOR_OUTREACH_CANCEL_ROUTE);
  };

  // Team members get no Manage Plan. While a cancellation is pending, Do Not Cancel replaces it.
  const showManagePlan = isPrimaryHolder && isActive && !isPendingCancel;

  const doNotCancel = () => {
    props.leaveMessagePopup({
      title: 'Restore Investor Outreach Subscription',
      message: connectAlsoCancelled ? (
        <>
          By selecting <strong>Confirm</strong>, you will restore both your Investor Outreach and
          Propstream Connect subscription. Would you like to proceed?
        </>
      ) : (
        <>
          By selecting <strong>Confirm</strong>, you will restore your Investor Outreach
          subscription. Your PropStream Connect subscription will continue as originally scheduled.
        </>
      ),
      yesLable: 'Confirm',
      cancelLable: 'Cancel',
      yesRedirect: async () => {
        const result = await restore({ userId });
        if (result.ok) {
          // The server says whether Connect came back with it (restore after Connect was cancelled too).
          showToast(
            result.data?.connectRestored
              ? 'Investor Outreach and PropStream Connect subscriptions restored successfully'
              : 'Investor Outreach subscription restored successfully',
            'success'
          );
          // Restore can bring Connect back or move it back to the included plan: refresh both cards.
          refreshSubscriptionCards(dispatch, userId);
        } else {
          showToast(result.message || 'Something went wrong, Please try again later', 'error');
        }
      },
      bodyCss: css.doNotCancelBody,
      btnCss: css.doNotCancelBtn,
    });
  };

  return (
    <div className={classNames(css.clsCard, css.clsInvestorOutreachCard)}>
      <LoadablePanel loading={isLoading}>
        {/* Like the other cards, only the loader shows until the subscription state is known. */}
        <div
          className={classNames(css.clsCardHeader)}
          style={isLoading ? { display: 'none' } : undefined}
        >
          <div
            className={classNames(css.d_flex, css.align_items_center, css.justify_content_between)}
          >
            <h4 className={classNames(css.clsCardTitle)}>Investor Outreach</h4>

            {/* Plain text link per Figma, not a pill. */}
            {!isLoading && !isActive && (
              <a href="javascript:;" className={css.clsIOHeaderLink} onClick={goToPurchasePage}>
                Subscribe Now
              </a>
            )}

            {!isLoading && showManagePlan && (
              <div className={css.clsManagePlanWrapper}>
                {/* Pill button per Figma (node 1114:75536). Own class: the shared .btn only works inside .btnContainer. */}
                <a
                  href="javascript:;"
                  className={classNames(css.clsManagePlanBtn, { [css.isOpen]: isManagePlanOpen })}
                  onClick={() => setIsManagePlanOpen((open) => !open)}
                >
                  Manage Plan
                </a>
                <PopoverMenu
                  isOpen={isManagePlanOpen}
                  onClose={() => setIsManagePlanOpen(false)}
                  position="right"
                  customWidth={158}
                  items={[{ label: 'Unsubscribe', onClick: handleUnsubscribe }]}
                />
              </div>
            )}
          </div>
        </div>

        {!isLoading && isPendingCancel && (
          <div className={css.clsIOBanner}>
            <SVG icon="info-circle-white" size={36} />
            <p>
              Your Investor Outreach subscription will end on {moment(endDate).format('MM/DD/YYYY')}
            </p>
            {isPrimaryHolder && (
              <button type="button" className={css.clsIOBannerBtn} onClick={doNotCancel}>
                Do Not Cancel
              </button>
            )}
          </div>
        )}
        {/* Ended: grey and informational like Connect's ended banner. No warning styling, no action. */}
        {!isLoading && !isActive && endedDate && (
          <div className={classNames(css.info, css.infoMaxWidth)}>
            <SVG icon="info-circle" size={36} />
            <p>
              Your Investor Outreach subscription has ended on{' '}
              {moment(endedDate).format('MM/DD/YYYY')}
            </p>
          </div>
        )}

        {/* Room for the card loader while the subscription state loads, like the other cards. */}
        {isLoading && <div className={css.clsCardBody} style={{ minHeight: 400 }} />}

        {!isLoading && !isActive && (
          <div className={css.clsCardBody}>
            <div className={css.clsLeadContentCard}>
              <div className={css.clsLeadContentList}>
                <div className={css.clsIOTextCol}>
                  <img src={investorOutreachLogo} alt="Investor Outreach" width={210} height={79} />
                  <p className={css.clsIOBody}>
                    Unlock off-market buyers instantly. Use Investor Outreach to find, analyze, and
                    contact active investors in your market—all without leaving PropStream.
                  </p>
                  <p className={css.clsIOFootnote}>
                    *Requires an active PropStream Connect subscription
                  </p>
                </div>
                <div className={css.clsLeadContenRight}>
                  <img src={investorOutreachIllustration} alt="" width={185} height={91} />
                </div>
              </div>

              <div className={classNames(css.btnContainer, 'text-right')}>
                <button className={classNames(css.btn)} onClick={goToPurchasePage}>
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>
        )}

        {!isLoading && isActive && (
          <div className={css.clsCardBody}>
            <div className={css.clsLeadContentCard}>
              <div className={css.clsLeadContentList}>
                <div className={css.clsIOTextCol}>
                  <img src={investorOutreachLogo} alt="Investor Outreach" width={210} height={79} />
                  <p className={css.clsIOBody}>
                    You have full access to investor portfolio analysis, active buyer
                    identification, and built-in outreach tools powered by PropStream Connect.
                  </p>
                </div>
                <div className={css.clsLeadContenRight}>
                  <img src={investorOutreachIllustration} alt="" width={185} height={91} />
                </div>
              </div>

              <div className={classNames(css.btnContainer, 'text-right')}>
                <button className={classNames(css.btn)} onClick={goToInvestorOutreach}>
                  Go to Investor Outreach
                </button>
              </div>
            </div>
          </div>
        )}
      </LoadablePanel>
    </div>
  );
};

export default withRouter(
  connect(null, {
    leaveMessagePopup: LeaveMessagePopup.open,
  })(InvestorOutreach)
);
